import axios from "axios";
import { aiClient, rmsClient } from "@/shared/api/clients";

/** Raw /templates response — callers own the array/object-shape normalization. */
export async function fetchTemplatesRaw(
  stationId: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await rmsClient.get("/templates", { params: { stationId }, signal });
  return response.data;
}

export async function deleteTemplate(input: {
  templateId: string;
  stationId: string | null;
}): Promise<void> {
  await rmsClient.delete("/template", { data: input });
}

/** Covers both rename and sign-off — pass the exact payload the server expects. */
export async function saveTemplate(body: {
  template: Record<string, unknown>;
  examples: string[];
}): Promise<void> {
  await rmsClient.put("/template", body);
}

export interface TemplateUploadInitResponse {
  jobId: string;
  uploads: { fileName: string; presignedUrl: string }[];
}

export async function initTemplateUpload(
  files: { fileName: string; fileSize: number; contentType: string }[],
): Promise<TemplateUploadInitResponse> {
  const { data } = await aiClient.post("/ai/upload-template", { files });
  return data;
}

export async function processTemplate(jobId: string): Promise<void> {
  await aiClient.post("/ai/process-template", { jobId });
}

export interface EditTemplateResponse {
  template: string;
  generatedExamples: string[];
  summary: string;
}

export async function editTemplate(input: {
  jobId: string;
  message: string;
}): Promise<EditTemplateResponse> {
  const { data } = await aiClient.post("/ai/edit-template", input);
  return data;
}

export interface TemplateJobResponse {
  status: "pending" | "processing" | "completed" | "failed" | string;
  result?: { template: string; generatedExamples?: string[] };
  error?: string;
}

export async function getTemplateJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<TemplateJobResponse> {
  const { data } = await aiClient.get("/ai/get-template-job", { params: { jobId }, signal });
  return data;
}

/**
 * Deliberately unauthenticated relative GET — this endpoint has never been
 * sent a token and must not start receiving one. Returns the raw response so
 * callers can read content-disposition.
 */
export async function downloadTemplateBlob(templateId: string) {
  return axios.get(`/api/templates/downloadTemplate/${encodeURIComponent(templateId)}`, {
    responseType: "blob",
  });
}
